import { useState, useEffect } from "react";
import { api } from "../App";

export default function IdleScreensaver({ onDismiss }) {
  const [wins, setWins] = useState([]);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ briefs: 0, leads: 0, tasks: 0 });
  const [currentWin, setCurrentWin] = useState(0);
  const [currentEvent, setCurrentEvent] = useState(0);
  const [time, setTime] = useState("");

  useEffect(() => {
    // Fetch data
    api.get("/notifications/team-wins?limit=8").then(r => setWins(r.data || [])).catch(() => {});
    api.get("/events/feed?limit=15").then(r => setEvents(r.data || [])).catch(() => {});
    Promise.all([
      api.get("/bizdev/briefs?limit=500").catch(() => ({ data: [] })),
      api.get("/leads?limit=500").catch(() => ({ data: [] })),
      api.get("/tasks?limit=500").catch(() => ({ data: [] })),
    ]).then(([b, l, t]) => {
      const briefsActive = (b.data || []).filter(x => !["Won", "Executed", "Lost"].includes(x.status)).length;
      const leadsActive = (l.data || []).filter(x => x.status !== "lost").length;
      const tasksOpen = (t.data || []).filter(x => x.status !== "Done").length;
      setStats({ briefs: briefsActive, leads: leadsActive, tasks: tasksOpen });
    });
  }, []);

  // Rotate wins
  useEffect(() => {
    if (wins.length <= 1) return;
    const i = setInterval(() => setCurrentWin(c => (c + 1) % wins.length), 5000);
    return () => clearInterval(i);
  }, [wins]);

  // Rotate events
  useEffect(() => {
    if (events.length <= 1) return;
    const i = setInterval(() => setCurrentEvent(c => (c + 1) % events.length), 3000);
    return () => clearInterval(i);
  }, [events]);

  // Clock
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" }));
    tick(); const i = setInterval(tick, 1000); return () => clearInterval(i);
  }, []);

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = (new Date() - new Date(dateStr)) / 1000;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div onClick={onDismiss} onKeyDown={onDismiss} tabIndex={0}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "radial-gradient(ellipse at 50% 30%, rgba(6,182,212,.04) 0%, #020204 70%)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        cursor: "pointer", animation: "dmFadeUp .6s ease-out",
      }}>

      {/* Ambient glow */}
      <div style={{ position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(227,25,55,.03) 0%, transparent 70%)", pointerEvents: "none" }} />

      {/* Clock */}
      <div style={{ fontFamily: "var(--font-orb)", fontSize: 72, color: "var(--t1)", letterSpacing: ".15em", fontWeight: 700, marginBottom: 8, opacity: .9 }}>{time}</div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--t3)", letterSpacing: ".3em", textTransform: "uppercase", marginBottom: 60 }}>DREAM MACHINE</div>

      {/* Stats row */}
      <div style={{ display: "flex", gap: 40, marginBottom: 50 }}>
        {[
          { label: "ACTIVE BRIEFS", value: stats.briefs, color: "var(--c)" },
          { label: "ACTIVE LEADS", value: stats.leads, color: "var(--g)" },
          { label: "OPEN TASKS", value: stats.tasks, color: "var(--a)" },
        ].map(s => (
          <div key={s.label} style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "var(--font-orb)", fontSize: 28, color: s.color, fontWeight: 700, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--t3)", letterSpacing: ".2em" }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Recent wins carousel */}
      {wins.length > 0 && (
        <div style={{ textAlign: "center", marginBottom: 40, minHeight: 50 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--t3)", letterSpacing: ".2em", marginBottom: 8 }}>RECENT WINS</div>
          <div key={currentWin} style={{ fontFamily: "var(--font-sans)", fontSize: 16, color: "#22C55E", animation: "dmFadeUp .4s ease-out", maxWidth: 500 }}>
            {(wins[currentWin]?.message || "").replace(/^🎉\s*/, "")}
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--t3)", marginTop: 6 }}>
            {timeAgo(wins[currentWin]?.created_at)}
          </div>
        </div>
      )}

      {/* Activity pulse */}
      {events.length > 0 && (
        <div style={{ textAlign: "center", minHeight: 40, maxWidth: 450 }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 9, color: "var(--t3)", letterSpacing: ".2em", marginBottom: 8 }}>TEAM PULSE</div>
          <div key={currentEvent} style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--t2)", animation: "dmFadeUp .3s ease-out", lineHeight: 1.5 }}>
            {events[currentEvent]?.summary}
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--t4)", marginTop: 4 }}>
            {events[currentEvent]?.actor_name} · {timeAgo(events[currentEvent]?.created_at)}
          </div>
        </div>
      )}

      {/* Dismiss hint */}
      <div style={{ position: "absolute", bottom: 40, fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--t4)", letterSpacing: ".15em", animation: "dmPip 2s ease-in-out infinite" }}>
        CLICK OR PRESS ANY KEY TO RESUME
      </div>
    </div>
  );
}
